import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { ModalRoute } from "../../components/routing/ModalRoute";
import {
  PitchContextFieldLabel,
  PITCH_CONTEXT_FIELD_PLACEHOLDER,
} from "../../components/brand/PitchContextFieldLabel";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import Input from "../../components/ui/Input";
import { brandProfileService } from "../../services/brandProfile";

const emptyForm = () => ({
  company_name: "",
  website_url: "",
  core_offering: "",
  icp: "",
  custom_context: "",
  brand_context: {},
});

const BrandNewModalContent = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [newForm, setNewForm] = useState(emptyForm);
  const [step, setStep] = useState("url");
  const [prefillLoading, setPrefillLoading] = useState(false);
  const websiteUrlInputRef = useRef(null);

  useEffect(() => {
    if (step !== "url") return;
    const id = requestAnimationFrame(() => {
      websiteUrlInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [step]);

  const createMutation = useMutation({
    mutationFn: (payload) => brandProfileService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brand-profiles-all"] });
      queryClient.invalidateQueries({ queryKey: ["brand-profile"] });
      navigate("/settings/brand/");
    },
  });

  const openDetails = (patch = {}) => {
    setNewForm((f) => ({ ...f, ...patch }));
    setStep("details");
  };

  const handleNoWebsite = () => {
    openDetails();
  };

  const handleContinue = async () => {
    const raw = newForm.website_url.trim();
    if (!raw) return;
    setPrefillLoading(true);
    try {
      const data = await brandProfileService.prefillFromWebsite(raw);
      const p = data.prefill || {};
      const snippet = (data.webpage_content && String(data.webpage_content).trim()) || "";
      openDetails({
        website_url: data.website_url || "",
        company_name: p.company_name ?? "",
        core_offering: p.core_offering ?? "",
        icp: p.icp ?? "",
        brand_context: snippet ? { webpage_content: data.webpage_content } : {},
      });
    } catch (err) {
      const body = err?.response?.data;
      const canonical = body?.website_url;
      openDetails({
        ...(canonical ? { website_url: canonical } : {}),
        company_name: "",
        core_offering: "",
        icp: "",
        brand_context: {},
      });
    } finally {
      setPrefillLoading(false);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (step === "url") {
      if (!newForm.website_url.trim() || prefillLoading) return;
      void handleContinue();
      return;
    }
    createMutation.mutate(newForm);
  };

  const urlContinueDisabled = !newForm.website_url.trim() || prefillLoading;
  const saveLoading = createMutation.isPending;

  return (
    <Card className="max-w-lg w-full p-6 relative">
      <button
        type="button"
        className="absolute top-4 right-4 text-theme-dim hover:text-theme-text font-mono text-xs"
        onClick={() => navigate("/settings/brand/")}
      >
        Close
      </button>
      <h3 className="font-display font-bold italic text-xl text-theme-on-background mb-1 pr-16">New brand</h3>
      <p className="font-mono text-[11px] text-theme-muted mb-5">
        Add a brand profile. We backfill scores in the background.
      </p>
      <form className="space-y-4" onSubmit={handleFormSubmit}>
        <Input
          ref={websiteUrlInputRef}
          label="Website URL"
          value={newForm.website_url}
          onChange={(e) => setNewForm((f) => ({ ...f, website_url: e.target.value }))}
          placeholder="yoururl.com"
        />
        {step === "details" ? (
          <>
            <Input
              label="Company name"
              value={newForm.company_name}
              onChange={(e) => setNewForm((f) => ({ ...f, company_name: e.target.value }))}
              placeholder="e.g. FlipMail"
            />
            <Input
              label="Product description"
              multiline
              value={newForm.core_offering}
              onChange={(e) => setNewForm((f) => ({ ...f, core_offering: e.target.value }))}
              placeholder="Describe what your product does..."
            />
            <Input
              label="Ideal customer profile"
              multiline
              value={newForm.icp}
              onChange={(e) => setNewForm((f) => ({ ...f, icp: e.target.value }))}
              placeholder="Who exactly are we pitching?"
            />
            <Input
              label={<PitchContextFieldLabel />}
              multiline
              value={newForm.custom_context}
              onChange={(e) => setNewForm((f) => ({ ...f, custom_context: e.target.value }))}
              placeholder={PITCH_CONTEXT_FIELD_PLACEHOLDER}
            />
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <Button type="submit" size="sm" loading={saveLoading}>
                Create brand
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <Button type="button" variant="ghost" size="sm" onClick={handleNoWebsite}>
              I dont have a website
            </Button>
            <Button type="submit" size="sm" loading={prefillLoading} disabled={urlContinueDisabled}>
              Continue
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
};

export default function SettingsBrandNew() {
  return (
    <ModalRoute closeTo="/settings/brand/">
      <BrandNewModalContent />
    </ModalRoute>
  );
}
